/**
 * Public Share Service
 *
 * Unauthenticated, token-based reads for shared maps. Uses the dedicated
 * {@link publicApiClient} (no workspace header, no session-expiry interceptor).
 *
 * Data is split across three reads: a slim LIST (left panel rows), a MAP
 * payload (geometry + anchor + tooltip metadata), and per-artifact DETAIL
 * fetches (full task/note/obstruction, on open). Viewer filters are sent as
 * comma-separated query params and intersected server-side with the share's
 * frozen base filters.
 */
import type { FeatureCollection } from 'geojson';

import { publicApiClient } from './publicApiClient';
import { API_ENDPOINTS } from '@/constants/api';
import type {
  PublicShareConfig,
  PublicShareListData,
  PublicShareMapData,
  PublicTask,
  PublicNote,
  PublicObstruction,
  ShareViewerFilters,
} from '@/types/share';

/** Joins a string array into a comma-separated param, or undefined when empty. */
function toCsv(values?: string[]): string | undefined {
  if (!values || values.length === 0) return undefined;
  return values.join(',');
}

/** Serializes an optional boolean param to "true"/"false", or undefined when unset. */
function toBoolParam(value?: boolean): string | undefined {
  return value === undefined ? undefined : String(value);
}

/**
 * Builds the query-param object shared by the public LIST and MAP endpoints.
 * Both accept the same viewer-filter params, intersected server-side with the
 * share's frozen base filters.
 */
function buildViewerFilterParams(
  filters?: ShareViewerFilters
): Record<string, string | undefined> {
  return {
    taskStatuses: toCsv(filters?.taskStatuses),
    taskPriorities: toCsv(filters?.taskPriorities),
    taskSearch: filters?.taskSearch || undefined,
    taskDueDateFrom: filters?.taskDueDateFrom || undefined,
    taskDueDateTo: filters?.taskDueDateTo || undefined,
    noteColors: toCsv(filters?.noteColors),
    noteSearch: filters?.noteSearch || undefined,
    noteDateFrom: filters?.noteDateFrom || undefined,
    noteDateTo: filters?.noteDateTo || undefined,
    obstructionTypes: toCsv(filters?.obstructionTypes),
    obstructionStatuses: toCsv(filters?.obstructionStatuses),
    obstructionSeverities: toCsv(filters?.obstructionSeverities),
    obstructionImpacts: toCsv(filters?.obstructionImpacts),
    obstructionRoad: filters?.obstructionRoad || undefined,
    obstructionContractor: filters?.obstructionContractor || undefined,
    obstructionDateFrom: filters?.obstructionDateFrom || undefined,
    obstructionDateTo: filters?.obstructionDateTo || undefined,
    taskSortBy: filters?.taskSortBy || undefined,
    taskSortDescending: toBoolParam(filters?.taskSortDescending),
    noteSortBy: filters?.noteSortBy || undefined,
    noteSortDescending: toBoolParam(filters?.noteSortDescending),
    obstructionSortBy: filters?.obstructionSortBy || undefined,
    obstructionSortDescending: toBoolParam(filters?.obstructionSortDescending),
  };
}

export const publicShareService = {
  /** Fetch the public presentation config (and `requiresPassword`). */
  getShare: (token: string) =>
    publicApiClient.get<PublicShareConfig>(
      API_ENDPOINTS.PUBLIC_SHARES.BY_TOKEN(token)
    ),

  /**
   * Verify the share password. On success the server sets the short-lived
   * `share_pwd_<token>` cookie (SameSite=Lax) consumed by the data endpoint.
   */
  verifyPassword: (token: string, password: string) =>
    publicApiClient.post<{ verified: boolean }>(
      API_ENDPOINTS.PUBLIC_SHARES.VERIFY_PASSWORD(token),
      { password }
    ),

  /**
   * Fetch the slim, base ∩ viewer filtered artifact list (left panel rows).
   * Carries only the fields list rows render; full detail is fetched lazily.
   */
  getListData: (token: string, filters?: ShareViewerFilters) =>
    publicApiClient.get<PublicShareListData>(
      API_ENDPOINTS.PUBLIC_SHARES.LIST(token),
      { params: buildViewerFilterParams(filters) }
    ),

  /**
   * Fetch the map payload — anchors, geometries, and hover-tooltip metadata
   * for every base ∩ viewer filtered artifact. Drives the map layers/pins.
   */
  getMapData: (token: string, filters?: ShareViewerFilters) =>
    publicApiClient.get<PublicShareMapData>(
      API_ENDPOINTS.PUBLIC_SHARES.MAP(token),
      { params: buildViewerFilterParams(filters) }
    ),

  /** Fetch a single PII-stripped task detail (lazily, on artifact open). */
  getTaskDetail: (token: string, id: string) =>
    publicApiClient.get<PublicTask>(
      API_ENDPOINTS.PUBLIC_SHARES.TASK_DETAIL(token, id)
    ),

  /** Fetch a single PII-stripped note detail (lazily, on artifact open). */
  getNoteDetail: (token: string, id: string) =>
    publicApiClient.get<PublicNote>(
      API_ENDPOINTS.PUBLIC_SHARES.NOTE_DETAIL(token, id)
    ),

  /** Fetch a single PII-stripped obstruction detail (lazily, on artifact open). */
  getObstructionDetail: (token: string, id: string) =>
    publicApiClient.get<PublicObstruction>(
      API_ENDPOINTS.PUBLIC_SHARES.OBSTRUCTION_DETAIL(token, id)
    ),

  /**
   * Fetch the GeoJSON of a share-exposed custom layer (lazily, when the layer
   * becomes visible). The endpoint 302-redirects to a signed storage URL; axios
   * follows it transparently. The share password cookie travels automatically.
   */
  getCustomLayerGeoJson: (
    token: string,
    layerId: string,
    signal?: AbortSignal
  ) =>
    publicApiClient.get<FeatureCollection>(
      API_ENDPOINTS.PUBLIC_SHARES.CUSTOM_LAYER_GEOJSON(token, layerId),
      { signal }
    ),

  /** Register a view (fire-and-forget; failures are non-fatal). */
  registerView: (token: string) =>
    publicApiClient.post<void>(API_ENDPOINTS.PUBLIC_SHARES.VIEW(token)),

  /**
   * Same-origin URL of the public attachment download endpoint. Use with
   * `<a href download>` so the browser follows the 302 to the storage signed
   * URL natively (no CORS). The share password cookie travels automatically.
   */
  getAttachmentDownloadUrl: (token: string, attachmentId: string) =>
    API_ENDPOINTS.PUBLIC_SHARES.ATTACHMENT_DOWNLOAD(token, attachmentId),

  /**
   * Same-origin streaming preview — raw bytes for in-browser viewers (PDF /
   * DOCX / image). Bypasses the cross-origin redirect so axios arraybuffer
   * works without bucket CORS. The share password cookie travels automatically.
   */
  fetchAttachmentPreview: (token: string, attachmentId: string) =>
    publicApiClient.get<ArrayBuffer>(
      API_ENDPOINTS.PUBLIC_SHARES.ATTACHMENT_PREVIEW(token, attachmentId),
      { responseType: 'arraybuffer' }
    ),

  /** Same-origin URL of the public NOTE attachment download endpoint (302 → signed URL). */
  getNoteAttachmentDownloadUrl: (token: string, attachmentId: string) =>
    API_ENDPOINTS.PUBLIC_SHARES.NOTE_ATTACHMENT_DOWNLOAD(token, attachmentId),

  /** Same-origin streaming preview of a public NOTE attachment (raw bytes, no bucket CORS). */
  fetchNoteAttachmentPreview: (token: string, attachmentId: string) =>
    publicApiClient.get<ArrayBuffer>(
      API_ENDPOINTS.PUBLIC_SHARES.NOTE_ATTACHMENT_PREVIEW(token, attachmentId),
      { responseType: 'arraybuffer' }
    ),
};
